import {
  type Dispatch,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useEffect,
  useRef,
} from "react";

import { move } from "@dnd-kit/helpers";
import { PlusIcon } from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import type { ProjectBoardIdea } from "simplydo/interfaces/types/ideaTypes";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import { DndContext } from "components/lib/dnd/DndContext";
import { makeResult } from "components/lib/dnd/dndHelpers";

import { LaneWrapper, ProjectLane } from "./ProjectLane";

export type IIdeaCard = {
  id: string;
  idea: ProjectBoardIdea;
  lane?: string;
  order: number;
  laneMetaType?: OpenAPI.Schemas["projectLanes"]["metaType"];
  draggingDisabled?: boolean;
  impacts: { usesImpactStatementSystem: boolean; impactCount: number };
};

export type ILaneData = {
  id: string;
  order: number;
  title: string;
  label?: string;
  description?: string;
  metaType?: string;
  cards: IIdeaCard[];
  draggingDisabled?: boolean;
};

const BoardWrapper = styled.div`
  height: calc(100vh - 205px);
  display: flex;
`;

const BoardOverflow = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  flex: 1;
  width: 100%;
  height: 100%;
  gap: 12px;
  padding: 2px;
  padding-bottom: 10px;
`;

const NewLaneWrapper = styled(LaneWrapper)`
  padding: 0;
  border: 0;
  ${Button} {
    margin: 0;
    box-shadow: none;
  }
`;

type BoardProps = {
  loading: boolean;
  lanes: ILaneData[];
  setLanes: Dispatch<SetStateAction<ILaneData[]>>;
  onCardMove?: (ideaId: string, laneId: string, position: number) => void;
  onLaneMove?: (fromIndex: number, toIndex: number) => void;
  createLane?: () => void;
};

const Board = ({
  loading,
  lanes,
  setLanes,
  createLane,
  onLaneMove,
  onCardMove,
}: BoardProps) => {
  const boardRef = useRef<HTMLDivElement | null>(null);
  const boardScrollRef = useRef<HTMLDivElement | null>(null);
  const boardPanRef = useRef({ active: false, startX: 0, startScroll: 0 });
  const boardPanWindowCleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      boardPanWindowCleanupRef.current?.();
    },
    [],
  );

  const onBoardPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const t = e.target;
    if (!(t instanceof Element) || t.closest("[data-board-lane]")) return;
    const el = boardScrollRef.current;
    if (!el) return;

    boardPanWindowCleanupRef.current?.();

    const pointerId = e.pointerId;
    boardPanRef.current = {
      active: true,
      startX: e.clientX,
      startScroll: el.scrollLeft,
    };

    const root = document.documentElement;
    const prevUserSelect = root.style.userSelect;
    root.style.userSelect = "none";

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const p = boardPanRef.current;
      if (!p.active) return;
      const scrollEl = boardScrollRef.current;
      if (!scrollEl) return;
      scrollEl.scrollLeft = p.startScroll - (ev.clientX - p.startX);
    };

    const cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      root.style.userSelect = prevUserSelect;
      window.getSelection()?.removeAllRanges();
      boardPanRef.current.active = false;
      boardPanWindowCleanupRef.current = null;
    };

    const onEnd = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      cleanup();
    };

    boardPanWindowCleanupRef.current = cleanup;
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
  };

  const items = lanes.reduce(
    (acc, lane) => {
      acc[lane.id] = lane.cards;
      return acc;
    },
    {} as Record<string, IIdeaCard[]>,
  );

  const setItems = (items: Record<string, IIdeaCard[]>) => {
    setLanes((prev) =>
      prev.map((lane) => ({ ...lane, cards: items[lane.id] ?? [] })),
    );
  };

  useEffect(() => {
    window.scrollTo({ top: 2000, behavior: "smooth" });
  }, []);

  const snapshot = useRef<ILaneData[] | null>(null);
  return (
    <DndContext
      onDragStart={() => {
        snapshot.current = structuredClone(lanes);
      }}
      onDragOver={(event, _manager, setMoveResult) => {
        const { source, target } = event.operation;
        if (!target) return;
        if (source?.type === "lane") return;

        setMoveResult(makeResult(event));
        setItems(move(items, event));
      }}
      onDragEnd={(event, _manager, setMoveResult) => {
        const { source } = event.operation;

        if (source?.type === "lane") {
          setLanes((lanes) => move(lanes, event));
          setMoveResult(makeResult(event));
        }
      }}
      onDidMove={(moveResult) => {
        if (moveResult.sourceType === "lane") {
          onLaneMove?.(moveResult.sourceIndex, moveResult.targetIndex);
        } else if (moveResult.sourceType === "card") {
          if (!snapshot.current) {
            return;
          }
          const lane = snapshot.current.find(
            (lane: ILaneData) =>
              lane.id ===
              (moveResult.targetType === "card"
                ? moveResult.targetGroup
                : moveResult.targetId),
          );
          if (!lane) {
            return;
          }

          let targetLane = moveResult.targetId;
          let targetIndex = lane?.cards.length ?? 0;
          if (moveResult.targetType === "card") {
            targetIndex = moveResult.targetIndex;
            targetLane = lane.id;

            const card = lane?.cards[moveResult.targetIndex!];
            if (card) {
              targetIndex = card.order;
            }
          }

          onCardMove?.(moveResult.sourceId, targetLane, targetIndex);
          snapshot.current = null;
        }
      }}
    >
      <BoardWrapper className="board-wrapper" ref={boardRef}>
        <BoardOverflow
          ref={boardScrollRef}
          className="board-overflow"
          onPointerDown={onBoardPointerDown}
        >
          {lanes.map((lane, index) => (
            <ProjectLane
              key={lane.id}
              index={index}
              lane={lane}
              loading={loading}
            />
          ))}
          {createLane ? (
            <NewLaneWrapper data-board-lane="">
              <Button onClick={createLane} variant="outline">
                <Icon icon={PlusIcon} />
                Create a new lane
              </Button>
            </NewLaneWrapper>
          ) : null}
        </BoardOverflow>
      </BoardWrapper>
    </DndContext>
  );
};

export default Board;
